/*
  AN INTERNAL NOTE, ACTUALLY STORED.

  This dialog used to end in:

      onSave && onSave({ body, audience, pinned, context });
      toast('Note saved', 'success', contextLabel || 'Note added to timeline');

  `onSave` was undefined at its only live call site (EventDetailPageV2's "Add note"), so the first
  clause did nothing and the second announced a timeline entry that did not exist. It now writes to
  `POST /api/admin/comments/:entityType/:entityId` — `AdminComment`, whose enum has allowed
  `organization | user | event` since it was written and which only the KYC screen ever used. The
  toast is raised here, after the awaited write, and a failure keeps the dialog open with the typed
  text intact: losing an operator's paragraph because a request failed is its own small betrayal.

  TWO CONTROLS WERE REMOVED RATHER THAN KEPT. "Audience" (Operations / Finance / Risk / All) and
  "Pin to top of timeline" had nowhere to go — `AdminComment` stores the text and its author, and
  nothing anywhere reads an audience or a pin. Keeping them would have meant either dropping the
  operator's choice silently, or persisting a field no reader honours, which is the same lie one
  layer deeper. The sub-line no longer promises an audit-trail citation either: the note is stored
  with its author and timestamp, which is what is true.
*/
import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from '../../lib/buses';
import { createAdminNote } from '../../lib/backOfficeApi';
import { Modal } from '../../ui/Modal';
import { BRAND } from '../../lib/brand';

export const NoteComposerModal = ({ open, onClose, entityType, entityId, contextLabel, onSaved }) => {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Adjust state during render (not in an effect) when `open` changes — see
  // react.dev "Adjusting some state when a prop changes".
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (!open) { setBody(''); setBusy(false); setErr(null); }
  }
  if (!open) return null;

  // No entity means nothing to attach the note TO. The composer is opened from a hook, so a caller
  // that forgets to pass one would otherwise get a dialog that can only fail on submit.
  const canSave = Boolean(entityType && entityId && body.trim()) && !busy;

  const save = async () => {
    if (!canSave) return;
    setBusy(true); setErr(null);
    try {
      const row = await createAdminNote(entityType, entityId, body.trim());
      toast('Note saved', 'success', contextLabel || undefined);
      onSaved && onSaved(row);
      onClose();
    } catch (e) {
      setErr(e?.message || 'Could not save this note.');
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} width="max-w-[520px]" title="Add internal note" sub={contextLabel ? `On ${contextLabel}` : null}>
      <div className="space-y-3">
        <div>
          <label className="block text-[11px] font-medium text-[#1F2937] mb-1.5">Note</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={5} autoFocus placeholder="What changed, what to watch, who to follow up with…" className="w-full bg-white border border-[#E6E4E1] rounded-md px-2.5 py-2 text-[12.5px] focus:outline-none focus:border-[#D8D5CF] resize-none" />
          <div className="text-[10.5px] text-[#9CA3AF] mt-1">Visible only to {BRAND.name} staff · stored with your name and the time.</div>
        </div>
        {!entityType || !entityId
          ? <div className="text-[11.5px] text-[#8A6B23] bg-[#FDF9EF] border border-[#E6D9B8] rounded-md px-3 py-2">This note has nothing to attach to — reopen it from the record you want to annotate.</div>
          : null}
        {err && <div className="text-[11.5px] text-[#9B1F1F] bg-[#FDF2F2] border border-[#F0D6D6] rounded-md px-3 py-2">{err}</div>}
      </div>
      <div className="px-5 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7] -mx-5 -mb-4 mt-4 flex items-center justify-end gap-1.5 gap-y-2 flex-wrap">
        <button onClick={onClose} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED]">Cancel</button>
        <button onClick={save} disabled={!canSave} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] disabled:cursor-not-allowed text-white inline-flex items-center gap-1.5">
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{busy ? 'Saving…' : 'Save note'}
        </button>
      </div>
    </Modal>
  );
};
